import { useEffect, useRef } from 'react'
import axios from 'axios'
import { Button, Stack, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import {
  getFileCharacteristics,
  setEtlFiles,
  getQuestions,
  setQuestions,
  addQuestion,
  setLatestEtlStatus,
  goBackETLStep,
} from '../../../../store/actions'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import FsContainer from '../../common/Container'
import FsContactCard from '../../common/ContactCard'
import FsAlert, { FsAlertRef } from '../../../common/atoms/Alert'
import { useLoading } from '../../../../shared/hooks'
import ACTIONS from '../../../../store/constants'
import { etlAPI } from '../../../../shared/api/ETLAPI'
import FsQuestion from './FsQuestion'

const FsAdditionalInformation = ({
  setIsLoading,
}: {
  setIsLoading: (isLoading: boolean) => void
}) => {
  const { t } = useTranslation()

  const dispatch = useAppDispatch()
  const alertRef = useRef<FsAlertRef>(null)
  const { etlQuestions, etlFileCharacteristics, latestEtlStatus, etlFiles } = useAppSelector(
    (state) => state.currentEtl,
  )

  const { isFetching } = useLoading([
    ACTIONS.ETL.SET_ETL_FILES,
    ACTIONS.ETL.GET_FILE_CHARACTERISTICS,
    ACTIONS.ETL.GET_QUESTIONS,
    ACTIONS.ETL.DELETE_QUESTIONS,
    ACTIONS.ETL.SET_ETL_STATUS,
  ])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
    }
  }, [latestEtlStatus])

  const isCompleted = !etlQuestions?.find(
    (ques) => !ques.question || !ques.column || !ques.row || !ques.answer,
  )

  useEffect(() => {
    if (!etlQuestions) {
      dispatch(getQuestions())
    }
  }, [])

  useEffect(() => {
    if (!etlFileCharacteristics) {
      dispatch(getFileCharacteristics())
    }
  }, [etlFiles, etlFileCharacteristics])

  const handleAccept = async (shouldGoNext: boolean) => {
    try {
      setIsLoading(true)
      if (!shouldGoNext) {
        await dispatch(setQuestions({ additionalInfo: etlQuestions, isCompleted: false }))
        alertRef.current?.openAlert({
          severity: 'success',
          message: t('question.saved'),
        })
      } else if (shouldGoNext) {
        if (isCompleted) {
          await dispatch(setQuestions({ additionalInfo: etlQuestions, isCompleted }))
          dispatch(setLatestEtlStatus())
        } else {
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('something.went.wrong'),
          })
        }
      }
    } catch (error) {
      alertRef.current?.openAlert({
        severity: 'error',
        message: t('something.went.wrong'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const downloadFile = async () => {
    if (latestEtlStatus) {
      try {
        setIsLoading(true)
        const downloadURL: any = await etlAPI.downloadEtlProcessedFile(latestEtlStatus.id)
        if (downloadURL && downloadURL.url) {
          const response = await axios({
            url: downloadURL.url,
            method: 'GET',
            responseType: 'blob',
            timeout: 180000,
          })
          if (response) {
            const blobUrl = URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = 'processed_file.csv'
            document.body.appendChild(link)
            link.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
              }),
            )
            setTimeout(() => {
              document.body.removeChild(link)
            }, 100)
          }
        } else {
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('file.download.fail'),
          })
        }
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('file.download.fail') })
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <FsContainer
      footer={
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Stack>
            <Button onClick={() => dispatch(goBackETLStep())}>{t('back')}</Button>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button variant="outlined" onClick={() => handleAccept(false)}>
              {t('save')}
            </Button>
            <Button variant="contained" onClick={() => handleAccept(true)} disabled={!isCompleted}>
              {t('save.continue')}
            </Button>
          </Stack>
        </Stack>
      }
    >
      <Typography mt={1}>{t('additional.information.sub')}</Typography>

      <Stack alignItems="flex-start" gap={2} mt={2} width={800}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          {t('download.processed.file')}
          <Button variant="outlined" onClick={downloadFile}>
            Download
          </Button>
        </Stack>
        <FsContactCard />
        <Typography variant="h5">Questions</Typography>
        {etlQuestions?.map((question, index) => (
          <FsQuestion
            key={index}
            question={question}
            setIsLoading={setIsLoading}
            alertRef={alertRef}
          />
        ))}
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          fullWidth
          onClick={() => dispatch(addQuestion())}
        >
          {t('question.add')}
        </Button>
      </Stack>
      <FsAlert ref={alertRef} />
    </FsContainer>
  )
}

export default FsAdditionalInformation
